/** Rótulos PT-BR para campos WABA em `integrations.config` (WhatsApp Cloud). */

export type WhatsAppCloudConfig = Record<string, unknown> | null | undefined;

const QUALITY_LABELS: Record<string, string> = {
  GREEN: "Alta",
  YELLOW: "Média",
  RED: "Baixa",
  NA: "Indisponível",
};

const TIER_LABELS: Record<string, string> = {
  TIER_50: "50 conversas iniciadas / 24h",
  TIER_250: "250 conversas iniciadas / 24h",
  TIER_1K: "1.000 conversas iniciadas / 24h",
  TIER_10K: "10.000 conversas iniciadas / 24h",
  TIER_100K: "100.000 conversas iniciadas / 24h",
  TIER_UNLIMITED: "Ilimitado",
};

const NAME_STATUS_LABELS: Record<string, string> = {
  APPROVED: "Aprovado",
  PENDING: "Em análise",
  REJECTED: "Rejeitado",
  AVAILABLE_WITHOUT_REVIEW: "Disponível sem revisão",
  DECLINED: "Recusado",
  EXPIRED: "Expirado",
  NONE: "Não definido",
};

const ACCOUNT_EVENT_LABELS: Record<string, string> = {
  PARTNER_REMOVED: "Parceiro removido pela Meta",
  PARTNER_APP_UNINSTALLED: "App desinstalado pelo cliente",
  ACCOUNT_OFFBOARDED: "Conta desativada",
  ACCOUNT_DELETED: "Conta excluída",
  ACCOUNT_RESTRICTION: "Conta com restrição",
  ACCOUNT_VIOLATION: "Violação de políticas",
  ACCOUNT_REINSTATED: "Conta reativada",
};

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

export function getQualityRating(config: WhatsAppCloudConfig): string | null {
  return asString(config?.quality_rating) ?? asString(config?.current_quality_rating);
}

export function getMessagingLimitTier(config: WhatsAppCloudConfig): string | null {
  return (
    asString(config?.messaging_limit_tier) ??
    asString(config?.whatsapp_business_manager_messaging_limit) ??
    asString(config?.current_limit)
  );
}

export function getThroughputLevel(config: WhatsAppCloudConfig): string | null {
  return asString(config?.throughput_level);
}

export function getNameStatus(config: WhatsAppCloudConfig): string | null {
  return asString(config?.name_status);
}

export function getVerifiedName(config: WhatsAppCloudConfig): string | null {
  return asString(config?.verified_name);
}

export function qualityRatingLabel(rating: string | null): string {
  if (!rating) return "Não informado";
  return QUALITY_LABELS[rating.toUpperCase()] ?? rating;
}

export function messagingLimitTierLabel(tier: string | null): string {
  if (!tier) return "Não informado";
  const normalized = tier.toUpperCase().replace(/\s+/g, "_");
  return TIER_LABELS[normalized] ?? tier;
}

export function throughputLevelLabel(level: string | null): string {
  if (!level) return "Não informado";
  return level.replace(/_/g, " ");
}

export function nameStatusLabel(status: string | null): string {
  if (!status) return "Não informado";
  return NAME_STATUS_LABELS[status.toUpperCase()] ?? status;
}

export function isPhoneVerificationPending(config: WhatsAppCloudConfig): boolean {
  const status = asString(config?.code_verification_status);
  return !!status && status.toUpperCase() !== "VERIFIED";
}

export function isDisplayNamePending(config: WhatsAppCloudConfig): boolean {
  const status = getNameStatus(config);
  return status?.toUpperCase() === "PENDING";
}

export function isDisplayNameRejected(config: WhatsAppCloudConfig): boolean {
  const status = getNameStatus(config);
  return status?.toUpperCase() === "REJECTED" || status?.toUpperCase() === "DECLINED";
}

export function qualityRatingBadgeClass(rating: string | null): string {
  switch (rating?.toUpperCase()) {
    case "GREEN":
      return "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30";
    case "YELLOW":
      return "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30";
    case "RED":
      return "bg-destructive/15 text-destructive border-destructive/30";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

function formatAccountEvent(event: string): string {
  return ACCOUNT_EVENT_LABELS[event.toUpperCase()] ?? event;
}

function warningsFromAccountUpdateObject(obj: Record<string, unknown>): string[] {
  const warnings: string[] = [];
  const event = asString(obj.event);
  if (event) {
    let line = formatAccountEvent(event);
    const phone = asString(obj.phone_number);
    if (phone) line += ` (${phone})`;
    warnings.push(line);
  }

  const directWarning = asString(obj.warning) ?? asString(obj.message);
  if (directWarning) warnings.push(directWarning);

  const violation = obj.violation_info;
  if (violation && typeof violation === "object") {
    const v = violation as Record<string, unknown>;
    const type = asString(v.violation_type) ?? asString(v.type);
    if (type) warnings.push(`Violação: ${type}`);
  }

  const restrictions = obj.restriction_info;
  if (Array.isArray(restrictions)) {
    for (const item of restrictions) {
      if (!item || typeof item !== "object") continue;
      const r = item as Record<string, unknown>;
      const type = asString(r.restriction_type);
      if (type) warnings.push(`Restrição ativa: ${type.replace(/_/g, " ")}`);
    }
  }

  const ban = obj.ban_info;
  if (ban && typeof ban === "object") {
    const b = ban as Record<string, unknown>;
    const state = asString(b.waba_ban_state) ?? asString(b.state);
    if (state) warnings.push(`Status de bloqueio: ${state}`);
  }

  const disconnect = obj.disconnection_info;
  if (disconnect && typeof disconnect === "object") {
    const d = disconnect as Record<string, unknown>;
    const reason = asString(d.reason);
    const initiatedBy = asString(d.initiated_by);
    if (reason) {
      warnings.push(
        `Desconexão: ${reason.replace(/_/g, " ")}${initiatedBy ? ` (${initiatedBy})` : ""}`,
      );
    }
  }

  return warnings;
}

/** Avisos de `account_update` persistidos pelo webhook em `integrations.config`. */
export function getAccountUpdateWarnings(config: WhatsAppCloudConfig): string[] {
  if (!config) return [];

  const warnings: string[] = [];

  const single = asString(config.account_update_warning);
  if (single) warnings.push(single);

  const list = config.account_update_warnings;
  if (Array.isArray(list)) {
    for (const item of list) {
      const text = asString(item);
      if (text) warnings.push(text);
    }
  }

  const event = asString(config.account_update_event);
  if (event) {
    const payload: Record<string, unknown> = { event };
    for (const key of [
      "phone_number",
      "violation_info",
      "restriction_info",
      "disconnection_info",
      "ban_info",
    ] as const) {
      if (config[key] != null) payload[key] = config[key];
    }
    warnings.push(...warningsFromAccountUpdateObject(payload));
  } else {
    for (const key of [
      "violation_info",
      "restriction_info",
      "disconnection_info",
      "ban_info",
    ] as const) {
      const info = config[key];
      if (info && typeof info === "object") {
        warnings.push(...warningsFromAccountUpdateObject(info as Record<string, unknown>));
      }
    }
  }

  const nested = config.account_update ?? config.last_account_update;
  if (nested && typeof nested === "object") {
    warnings.push(...warningsFromAccountUpdateObject(nested as Record<string, unknown>));
  }

  const accountStatus = asString(config.account_status);
  if (
    !event &&
    accountStatus &&
    ["violation", "restricted", "partner_removed", "offboarded", "deleted"].includes(accountStatus)
  ) {
    warnings.push(`Status da conta: ${accountStatus.replace(/_/g, " ")}`);
  }

  return [...new Set(warnings)];
}

export function hasWabaStatusData(config: WhatsAppCloudConfig): boolean {
  return Boolean(
    getQualityRating(config) ||
      getMessagingLimitTier(config) ||
      getThroughputLevel(config) ||
      getNameStatus(config) ||
      getVerifiedName(config) ||
      getAccountUpdateWarnings(config).length > 0 ||
      isPhoneVerificationPending(config) ||
      isDisplayNamePending(config) ||
      isDisplayNameRejected(config),
  );
}
